/**
 * Regras do confronto para a NFS-e do padrão nacional (família SV-*; catálogo em public.fiscal_regra, documento 'nfse').
 * As contas seguem as fórmulas do Anexo I do leiaute SEFIN/ADN 1.01 (infNFSe/valores e infNFSe/IBSCBS); as de tributação
 * (ISS × imunidade, retenção, alíquota) seguem a LC 116/2003 e as regras de negócio do próprio Anexo I.
 *
 * O sistema emissor nacional já rejeita boa parte dessas incoerências, mas nem toda NFS-e passa por ele: as de municípios
 * com sistema próprio chegam ao ADN por compartilhamento, e a alíquota de município não conveniado é a que o emitente
 * informou. Por isso as regras rodam em toda NFS-e — e não acusam nada numa nota coerente.
 *
 * Só importa tipos de ./regras (sem ciclo em tempo de execução): quem chama passa a Avaliacao.
 */
import type { BaseConhecimento } from './base';
import { itemLc116 } from './nfse';
import type { Avaliacao } from './regras';
import type { ItemLido, NotaLida, ServicoLido } from './tipos';

const TOLERANCIA = 0.02;
const dinheiro = (v: number) => v.toFixed(2);
const arred = (v: number) => Math.round(v * 100) / 100;
const z = (v: number | null | undefined) => v ?? 0;
const retido = (s: ServicoLido) => s.tpRetISSQN === '2' || s.tpRetISSQN === '3';

/** Rótulos dos domínios da NFS-e (Anexo I) para as mensagens. */
export const TRIB_ISSQN: Record<string, string> = { '1': 'operação tributável', '2': 'imunidade', '3': 'exportação de serviço', '4': 'não incidência' };
export const RET_ISSQN: Record<string, string> = { '1': 'não retido', '2': 'retido pelo tomador', '3': 'retido pelo intermediário' };
export const SIMPLES_NFSE: Record<string, string> = { '1': 'não optante', '2': 'MEI', '3': 'ME/EPP do Simples' };

/** Itens da LC 116 com alíquota mínima de ISS abaixo de 2% permitida (LC 116, art. 8º-A, §1º: 7.02, 7.05 e 16.01). */
const EXCECOES_ALIQ_MINIMA = new Set(['0702', '0705', '1601']);

/**
 * SV-VALOR-01 — base de cálculo do ISSQN (Anexo I, infNFSe/valores/vBC):
 *   vBC = vServ − vDescIncond − (vDR ou vCalcDR) − (vRedBCBM ou vCalcBM) − vCalcReeRepRes.
 * O valor do ISS (vBC × alíquota) é conferido pela IT-VALOR-01, como na NF-e.
 */
function regraBaseIss(s: ServicoLido, av: Avaliacao) {
  if (s.vBC === null || s.vServ === null) return;
  const deducao = s.vDR ?? s.vCalcDR;
  const beneficio = s.vRedBCBM ?? s.vCalcBM;
  const esperado = arred(s.vServ - z(s.vDescIncond) - z(deducao) - z(beneficio) - z(s.ibscbs?.vCalcReeRepRes));
  if (Math.abs(esperado - s.vBC) <= TOLERANCIA) return;
  const partes = [`serviço ${dinheiro(s.vServ)}`];
  if (s.vDescIncond) partes.push(`desconto incondicionado ${dinheiro(s.vDescIncond)}`);
  if (deducao) partes.push(`dedução/redução ${dinheiro(deducao)}`);
  if (beneficio) partes.push(`benefício municipal ${dinheiro(beneficio)}`);
  if (s.ibscbs?.vCalcReeRepRes) partes.push(`reembolso/repasse ${dinheiro(s.ibscbs.vCalcReeRepRes)}`);
  av.add({
    regra: 'SV-VALOR-01',
    resultado: 'DIVERGENTE',
    mensagem: `Base do ISS: ${partes.join(' − ')} dá ${dinheiro(esperado)}, mas a base informada é ${dinheiro(s.vBC)}.`,
    encontrado: dinheiro(s.vBC),
    esperado: dinheiro(esperado),
  });
}

/**
 * SV-VALOR-02 — retenções e valor líquido (Anexo I, infNFSe/valores):
 *   vTotalRet = vRetCP + vRetIRRF + vRetCSLL + vISSQN (este só quando retido);
 *   vLiq = vServ − vDescIncond − vDescCond − vTotalRet;
 *   vTotNF (IBSCBS/totCIBS) = vLiq em 2026 (IBS e CBS por fora a partir de 2027 — não conferido aqui).
 */
function regraLiquido(s: ServicoLido, av: Avaliacao) {
  const issRetido = retido(s) ? z(s.vISSQN) : 0;
  const retencoes = arred(z(s.vRetCP) + z(s.vRetIRRF) + z(s.vRetCSLL) + issRetido);
  if (s.vTotalRet !== null && Math.abs(retencoes - s.vTotalRet) > TOLERANCIA) {
    av.add({
      regra: 'SV-VALOR-02',
      resultado: 'DIVERGENTE',
      mensagem: `Total retido: INSS ${dinheiro(z(s.vRetCP))} + IRRF ${dinheiro(z(s.vRetIRRF))} + CSLL ${dinheiro(z(s.vRetCSLL))}${retido(s) ? ` + ISS retido ${dinheiro(issRetido)}` : ''} dá ${dinheiro(retencoes)}, mas o total informado é ${dinheiro(s.vTotalRet)}.`,
      encontrado: dinheiro(s.vTotalRet),
      esperado: dinheiro(retencoes),
    });
  }
  if (s.vLiq === null || s.vServ === null) return;
  const totalRet = s.vTotalRet ?? retencoes;
  const liquido = arred(s.vServ - z(s.vDescIncond) - z(s.vDescCond) - totalRet);
  if (Math.abs(liquido - s.vLiq) > TOLERANCIA) {
    av.add({
      regra: 'SV-VALOR-02',
      resultado: 'DIVERGENTE',
      mensagem: `Valor líquido: serviço ${dinheiro(s.vServ)} − descontos ${dinheiro(z(s.vDescIncond) + z(s.vDescCond))} − retenções ${dinheiro(totalRet)} dá ${dinheiro(liquido)}, mas o líquido informado é ${dinheiro(s.vLiq)}.`,
      encontrado: dinheiro(s.vLiq),
      esperado: dinheiro(liquido),
    });
  }
  const ano = Number((s.dCompet ?? '').slice(0, 4));
  const vTotNF = s.ibscbs?.vTotNF ?? null;
  if (ano === 2026 && vTotNF !== null && Math.abs(vTotNF - s.vLiq) > TOLERANCIA) {
    av.add({
      regra: 'SV-VALOR-02',
      resultado: 'DIVERGENTE',
      mensagem: `Em 2026 o valor total da NFS-e (vTotNF) deve ser igual ao valor líquido (${dinheiro(s.vLiq)}), mas a nota informa ${dinheiro(vTotNF)}.`,
      encontrado: dinheiro(vTotNF),
      esperado: dinheiro(s.vLiq),
    });
  }
}

/**
 * SV-IBS-01 — base e valores do IBS/CBS calculados na NFS-e (Anexo I, infNFSe/IBSCBS):
 *   base: vServ − vDescIncond − vCalcReeRepRes − vISSQN − vPIS − vCOFINS (competência 2026; de 2027 a 2032 sem PIS/COFINS);
 *   valores: base × alíquota × (1 − redução %), para IBS estadual, IBS municipal e CBS; vIBSTot = IBS UF + IBS Mun.
 */
function regraIbsCbs(s: ServicoLido, item: ItemLido, av: Avaliacao) {
  const g = item.ibscbs;
  if (!g || !s.ibscbs || g.vBC === null || s.vServ === null) return;
  const ano = Number((s.dCompet ?? '').slice(0, 4));
  if (ano >= 2026 && ano <= 2032) {
    const pisCofins = ano === 2026 ? z(item.pis?.vTributo) + z(item.cofins?.vTributo) : 0;
    const base = arred(s.vServ - z(s.vDescIncond) - z(s.ibscbs.vCalcReeRepRes) - z(s.vISSQN) - pisCofins);
    if (Math.abs(base - g.vBC) > TOLERANCIA) {
      av.add({
        regra: 'SV-IBS-01',
        resultado: 'DIVERGENTE',
        mensagem: `Base do IBS/CBS: serviço ${dinheiro(s.vServ)} − desconto incondicionado ${dinheiro(z(s.vDescIncond))} − reembolso/repasse ${dinheiro(z(s.ibscbs.vCalcReeRepRes))} − ISS ${dinheiro(z(s.vISSQN))}${ano === 2026 ? ` − PIS/COFINS ${dinheiro(pisCofins)}` : ''} dá ${dinheiro(base)}, mas a base informada é ${dinheiro(g.vBC)}.`,
        encontrado: dinheiro(g.vBC),
        esperado: dinheiro(base),
      });
    }
  }
  const conferir = (rotulo: string, aliquota: number | null, reducao: number | null, valor: number | null) => {
    if (aliquota === null || valor === null || g.vBC === null) return;
    const calculado = arred((g.vBC * aliquota * (1 - z(reducao) / 100)) / 100);
    if (Math.abs(calculado - valor) <= TOLERANCIA) return;
    av.add({
      regra: 'SV-IBS-01',
      resultado: 'DIVERGENTE',
      mensagem: `${rotulo}: base × alíquota (${dinheiro(g.vBC)} × ${aliquota}%${reducao ? `, redução de ${reducao}%` : ''}) dá ${dinheiro(calculado)}, mas o valor informado é ${dinheiro(valor)}.`,
      encontrado: dinheiro(valor),
      esperado: dinheiro(calculado),
    });
  };
  conferir('IBS estadual', g.pIBSUF, g.pRedIBSUF, g.vIBSUF);
  conferir('IBS municipal', g.pIBSMun, g.pRedIBSMun, g.vIBSMun);
  conferir('CBS', g.pCBS, g.pRedCBS, g.vCBS);
  const tot = s.ibscbs.vIBSTot;
  if (tot !== null && g.vIBSUF !== null && g.vIBSMun !== null && Math.abs(arred(g.vIBSUF + g.vIBSMun) - tot) > TOLERANCIA) {
    av.add({
      regra: 'SV-IBS-01',
      resultado: 'DIVERGENTE',
      mensagem: `IBS total: estadual ${dinheiro(g.vIBSUF)} + municipal ${dinheiro(g.vIBSMun)} dá ${dinheiro(arred(g.vIBSUF + g.vIBSMun))}, mas o total informado é ${dinheiro(tot)}.`,
      encontrado: dinheiro(tot),
      esperado: dinheiro(arred(g.vIBSUF + g.vIBSMun)),
    });
  }
}

/**
 * SV-ISS-01 — tributação do ISS coerente com o valor:
 *   imunidade, exportação ou não incidência (tribISSQN 2/3/4) não têm ISS a pagar nem retenção (Anexo I, E0580);
 *   operação tributável sem ISS calculado, fora de MEI, Simples com ISS no DAS, isenção, alíquota zero do benefício
 *   municipal ou exigibilidade suspensa, pede revisão.
 */
function regraTributacaoIss(s: ServicoLido, av: Avaliacao) {
  const trib = s.tribISSQN;
  if (!trib) return;
  const rotulo = TRIB_ISSQN[trib] ?? `tribISSQN ${trib}`;
  if (trib !== '1') {
    if (z(s.vISSQN) > 0) {
      av.add({ regra: 'SV-ISS-01', resultado: 'DIVERGENTE', mensagem: `Tributação do ISS informada como ${rotulo}, mas a nota calcula ISS de ${dinheiro(z(s.vISSQN))}.`, encontrado: dinheiro(z(s.vISSQN)), esperado: '0.00' });
    }
    if (retido(s)) {
      av.add({ regra: 'SV-ISS-01', resultado: 'DIVERGENTE', mensagem: `Tributação do ISS informada como ${rotulo}, que não admite retenção, mas a nota indica ISS ${RET_ISSQN[s.tpRetISSQN ?? ''] ?? s.tpRetISSQN}.`, encontrado: RET_ISSQN[s.tpRetISSQN ?? ''] ?? s.tpRetISSQN, esperado: 'não retido' });
    }
    return;
  }
  const semIss = !s.vISSQN && !s.pAliqAplic && !s.pAliq;
  const dispensado =
    s.prestador.opSimpNac === '2' ||
    (s.prestador.opSimpNac === '3' && s.prestador.regApTribSN !== '3') ||
    s.tpBM === '1' ||
    s.tpSusp !== null;
  if (semIss && !dispensado) {
    av.add({
      regra: 'SV-ISS-01',
      resultado: 'DEPENDE',
      severidade: 'alerta',
      mensagem: 'Operação tributável sem alíquota nem valor de ISS, e sem MEI, Simples, isenção municipal ou exigibilidade suspensa que explique.',
      encontrado: 'sem ISS',
    });
  }
}

/**
 * SV-ALIQ-01 — alíquota do ISS entre 2% e 5% (LC 116, arts. 8º, II, e 8º-A). Só para não optante do Simples (no Simples
 * a alíquota segue o anexo) e sem benefício municipal de alíquota diferenciada; itens 7.02, 7.05 e 16.01 podem ficar
 * abaixo de 2%. A alíquota do município em si é conferida quando a base tiver a alíquota aprovada dele.
 */
function regraFaixaAliquota(s: ServicoLido, av: Avaliacao) {
  const aliq = s.pAliqAplic ?? s.pAliq;
  if (aliq === null || aliq === 0 || s.tribISSQN !== '1' || s.prestador.opSimpNac !== '1' || s.tpBM === '4') return;
  const item = (s.cTribNac ?? '').slice(0, 4);
  if (aliq > 5) {
    av.add({ regra: 'SV-ALIQ-01', resultado: 'DIVERGENTE', mensagem: `Alíquota do ISS de ${aliq}% acima do máximo de 5% (LC 116, art. 8º, II).`, encontrado: `${aliq}%`, esperado: 'até 5%' });
  } else if (aliq < 2 && !EXCECOES_ALIQ_MINIMA.has(item)) {
    av.add({ regra: 'SV-ALIQ-01', resultado: 'DIVERGENTE', mensagem: `Alíquota do ISS de ${aliq}% abaixo do mínimo de 2% (LC 116, art. 8º-A).`, encontrado: `${aliq}%`, esperado: 'a partir de 2%' });
  }
}

/** SV-RET-01 — retenção do ISS exige quem retém: tomador (tpRetISSQN 2) ou intermediário (3) identificado. */
function regraRetencao(s: ServicoLido, av: Avaliacao) {
  if (s.tpRetISSQN === '2' && !s.tomador?.doc) {
    av.add({ regra: 'SV-RET-01', resultado: 'DIVERGENTE', mensagem: 'ISS retido pelo tomador, mas a nota não identifica o tomador (CNPJ/CPF).', encontrado: 'retido pelo tomador' });
  }
  if (s.tpRetISSQN === '3' && !s.intermediario?.doc) {
    av.add({ regra: 'SV-RET-01', resultado: 'DIVERGENTE', mensagem: 'ISS retido pelo intermediário, mas a nota não identifica o intermediário.', encontrado: 'retido pelo intermediário' });
  }
}

/**
 * SV-SIT-01 — situação da nota: cancelada por evento recebido no lote (alerta — não deve ser escriturada nem somada) ou
 * de substituição (informativo — a substituída deixa de valer).
 */
function regraSituacao(s: ServicoLido, av: Avaliacao) {
  const c = s.cancelamento;
  if (c) {
    av.add({
      regra: 'SV-SIT-01',
      resultado: 'DEPENDE',
      severidade: 'alerta',
      mensagem: `NFS-e cancelada${c.codigo === '105102' ? ' por substituição' : ''}${c.dhEvento ? ` em ${c.dhEvento.slice(0, 10).split('-').reverse().join('/')}` : ''}${c.motivo ? ` (${c.motivo})` : ''}: não deve ser escriturada.`,
      encontrado: `evento ${c.codigo}`,
    });
  }
  if (!s.chSubstda) return;
  av.add({ regra: 'SV-SIT-01', resultado: 'DEPENDE', severidade: 'info', mensagem: `Esta NFS-e substitui a de chave ${s.chSubstda}: a substituída não vale mais.`, encontrado: s.chSubstda });
}

// ---------------------------------------------------------------------------
// Regras que usam a base de serviços (fiscal_nfse_*): sem a tabela carregada, não rodam.
// ---------------------------------------------------------------------------

const ROTULO_INCIDENCIA = { prestador: 'no município do prestador', prestacao: 'no local da prestação', tomador: 'no município do tomador' } as const;

/** SV-CTRIB-01 — o código de tributação nacional existe na lista de serviço nacional (Anexo B). */
function regraCodigoServico(s: ServicoLido, base: BaseConhecimento, av: Avaliacao) {
  const lista = base.servicos?.servico;
  if (!lista?.size) return;
  if (!s.cTribNac) {
    av.add({ regra: 'SV-CTRIB-01', resultado: 'DIVERGENTE', mensagem: 'NFS-e sem código de tributação nacional (cTribNac).' });
  } else if (!lista.has(s.cTribNac)) {
    av.add({ regra: 'SV-CTRIB-01', resultado: 'DIVERGENTE', mensagem: `Código de tributação nacional ${s.cTribNac} não existe na lista de serviço nacional.`, encontrado: s.cTribNac });
  }
}

/**
 * SV-LOCAL-01 — município de incidência do ISS (LC 116, art. 3º, conforme o Anexo I): estabelecimento do prestador (regra
 * geral), local da prestação ou estabelecimento do tomador, pelo serviço. Fica fora o que tem regra própria: prestação
 * em águas marítimas (0000000), serviço no/do exterior e exportação.
 */
function regraLocalIncidencia(nota: NotaLida, s: ServicoLido, base: BaseConhecimento, av: Avaliacao) {
  const info = s.cTribNac ? base.servicos?.servico.get(s.cTribNac) : undefined;
  if (!info?.incidencia || !s.cLocIncid) return;
  if (s.tribISSQN === '3' || s.cPaisPrestacao || s.cLocPrestacao === '0000000') return;
  const esperado = info.incidencia === 'prestador' ? (nota.emit.enderEmit?.cMun ?? null) : info.incidencia === 'prestacao' ? s.cLocPrestacao : (s.tomador?.cMun ?? null);
  if (!esperado || !/^\d{7}$/.test(esperado) || esperado === s.cLocIncid) return;
  av.add({
    regra: 'SV-LOCAL-01',
    resultado: 'DIVERGENTE',
    mensagem: `O ISS do serviço ${s.cTribNac} incide ${ROTULO_INCIDENCIA[info.incidencia]} (${esperado}), mas a nota indica o município ${s.cLocIncid}${s.xLocIncid ? ` (${s.xLocIncid})` : ''}.`,
    encontrado: s.cLocIncid,
    esperado,
  });
}

/**
 * SV-NBS-01, SV-CCLASS-01 e SV-INDOP-01 — o que a nota informou × o que o Anexo VIII correlaciona ao item da LC 116 (guia de
 * referência do portal: fora da lista é DEPENDE, para olhar). cIndOp inexistente ou não aceito na NFS-e (Anexo VII) é
 * divergência.
 */
function regrasCorrelacao(s: ServicoLido, item: ItemLido, base: BaseConhecimento, av: Avaliacao) {
  const serv = base.servicos;
  if (!serv) return;
  const lc116 = itemLc116(s.cTribNac);
  const corr = lc116 ? serv.correlacao.get(lc116) : undefined;
  const lista = (codigos: Set<string>) => [...codigos].slice(0, 8).join(', ') + (codigos.size > 8 ? `… (${codigos.size})` : '');
  if (corr?.nbs.size && s.cNBS && !corr.nbs.has(s.cNBS)) {
    av.add({ regra: 'SV-NBS-01', resultado: 'DEPENDE', mensagem: `NBS ${s.cNBS} não está entre as NBS que o Anexo VIII correlaciona ao item ${lc116} da LC 116.`, encontrado: s.cNBS, esperado: lista(corr.nbs) });
  }
  const cct = item.ibscbs?.cClassTrib;
  if (corr?.cclassTrib.size && cct && !corr.cclassTrib.has(cct)) {
    av.add({ regra: 'SV-CCLASS-01', resultado: 'DEPENDE', mensagem: `cClassTrib ${cct} não está entre os que o Anexo VIII correlaciona ao item ${lc116} da LC 116.`, encontrado: cct, esperado: lista(corr.cclassTrib) });
  }
  const indOp = s.ibscbs?.cIndOp;
  if (!indOp) return;
  if (serv.cindOp.size) {
    const info = serv.cindOp.get(indOp);
    if (!info) {
      av.add({ regra: 'SV-INDOP-01', resultado: 'DIVERGENTE', mensagem: `cIndOp ${indOp} não existe na tabela de indicadores da operação (Anexo VII).`, encontrado: indOp });
      return;
    }
    if (!info.nfse) {
      av.add({ regra: 'SV-INDOP-01', resultado: 'DIVERGENTE', mensagem: `cIndOp ${indOp}${info.tipo ? ` (${info.tipo})` : ''} não é aceito na NFS-e (Anexo VII).`, encontrado: indOp });
      return;
    }
  }
  if (corr?.cindOp.size && !corr.cindOp.has(indOp)) {
    av.add({ regra: 'SV-INDOP-01', resultado: 'DEPENDE', severidade: 'alerta', mensagem: `cIndOp ${indOp} não está entre os que o Anexo VIII correlaciona ao item ${lc116} da LC 116.`, encontrado: indOp, esperado: lista(corr.cindOp) });
  }
}

/** Regras próprias da NFS-e, para o item (o serviço) da nota. */
export function avaliarServico(nota: NotaLida, item: ItemLido, base: BaseConhecimento, av: Avaliacao) {
  const s = nota.servico;
  if (!s) return;
  regraCodigoServico(s, base, av);
  regraLocalIncidencia(nota, s, base, av);
  regrasCorrelacao(s, item, base, av);
  regraBaseIss(s, av);
  regraLiquido(s, av);
  regraIbsCbs(s, item, av);
  regraTributacaoIss(s, av);
  regraFaixaAliquota(s, av);
  regraRetencao(s, av);
  regraSituacao(s, av);
}

/** Severidade padrão das regras SV-* (espelho do catálogo). */
export const SEVERIDADE_SERVICO = {
  'SV-VALOR-01': 'erro',
  'SV-VALOR-02': 'erro',
  'SV-IBS-01': 'erro',
  'SV-ISS-01': 'erro',
  'SV-ALIQ-01': 'alerta',
  'SV-RET-01': 'erro',
  'SV-SIT-01': 'info',
  'SV-CTRIB-01': 'erro',
  'SV-LOCAL-01': 'alerta',
  'SV-NBS-01': 'alerta',
  'SV-CCLASS-01': 'alerta',
  'SV-INDOP-01': 'erro',
} as const;
